import React, { FunctionComponent, useCallback, useContext, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { ReactNode } from 'react-markdown'
import { SafeAreaView } from 'react-native-safe-area-context'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { XIcon } from '../../atoms/icons/XIcon'
import { Link } from '../../atoms/link/Link'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { openUrl } from '../../utils/expo-linking'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { Modal } from '../modal/Modal'

const Container = styled(SafeAreaView)<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  flexGrow: 1,
  flexDirection: 'column',
  minHeight: '150px',
  gap: theme.spacing['24px'],
  padding: theme.breakpoints.isMobileSized ? (IS_WEB ? '24px' : '0px 24px') : '0px',
}))

const Header = styled.View<{
  hasTitle: boolean
}>(({ hasTitle }) => ({
  flexDirection: 'row',
  justifyContent: hasTitle ? 'space-between' : 'flex-end',
  backgroundColor: 'transparent',
  alignItems: 'flex-end',
  ...(IS_WEB && { cursor: 'default' }),
}))

const ContentsContainer = styled.View({
  zIndex: 1,
})

const ButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: theme.breakpoints.isMinWidthLaptop ? 'row-reverse' : 'column',
  justifyContent: 'flex-start',
  gap: theme.spacing['16px'],
}))

const CloseButton = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['24px'],
  alignSelf: 'center',
  ...(theme.breakpoints.isMobileSized && {
    marginBottom: theme.spacing['16px'],
    width: '100%',
  }),
}))

export interface CustomerTerminationModalProps {
  customerName?: string
  handleAnimationEnd?: () => void
  width?: string
  testID?: string
}

export const CustomerTerminationModal: FunctionComponent<CustomerTerminationModalProps> = ({
  customerName,
  handleAnimationEnd = () => {},
  width = '544px',
  testID = 'CustomerTerminationModal',
}) => {
  const { isCustomerTerminationModalEnabled } = useContext(AppContext)
  const { formatMessage } = useIntl()
  const { breakpoints, colors } = useTheme()
  const ref = useRef<BottomSheetGorhom>(null)
  const [isModalOpen, setIsModalOpen] = useState(true)

  if (!isCustomerTerminationModalEnabled || !customerName) {
    return null
  }

  const CUSTOMER_TERMINATION_COPY: Record<string, { title: string; body: ReactNode }> = {
    ['intuit']: {
      title: formatMessage({
        defaultMessage: 'Update on your Lyra benefit',
        description: 'Title for modal containing information about accessing Lyra',
      }),
      body: formatMessage(
        {
          defaultMessage:
            'As of August 1, 2025, your mental health benefits transitioned and the Lyra benefit is no longer available to Intuit employees and their families. To access your new benefit, visit <link>Intuit.SpringHealth.com</link>, Intuit’s mental health resources page, or contact Intuit HR Connect.',
          description: 'Body text for modal containing information about accessing Lyra',
        },
        {
          link: (text: string) => (
            <Link
              text={text}
              onPress={() => {
                openUrl('Intuit.SpringHealth.com')
              }}
            />
          ),
        },
      ),
    },
  }

  const closeBottomSheet = useCallback(() => {
    ref?.current?.close()
  }, [])

  const onRequestClose = () => {
    setIsModalOpen(false)
  }

  const modalTitle = formatMessage({
    defaultMessage: 'Update on your Lyra benefit',
    description: 'Title for modal containing information about accessing Lyra',
  })

  const modalContentsWithButtons = (
    <Container testID={tID(`${testID}-container`)}>
      <Header hasTitle={Boolean(modalTitle)} testID={tID(`${testID}-header`)}>
        <Title
          size={breakpoints.isMobileSized ? TitleSize.SMALL : TitleSize.MEDIUM}
          text={CUSTOMER_TERMINATION_COPY[customerName].title}
        />
        {breakpoints.isMinWidthTablet && (
          <PressableOpacity
            onPress={onRequestClose}
            accessibilityLabel={formatMessage({
              defaultMessage: 'Close modal',
              description: 'Accessibility label for close modal button',
            })}
          >
            <XIcon size={24} fillColor={colors.iconDefault} />
          </PressableOpacity>
        )}
      </Header>
      <ContentsContainer>
        <BodyText text={CUSTOMER_TERMINATION_COPY[customerName].body} size={BodyTextSize.DEFAULT} />
      </ContentsContainer>
      <ButtonContainer>
        <CloseButton
          onPress={onRequestClose}
          testID={tID(`${testID}-continueButton`)}
          text={formatMessage({
            defaultMessage: 'Close',
            description: 'Button text for closing modal',
          })}
        />
      </ButtonContainer>
    </Container>
  )

  return (
    <Modal
      modalTitle={modalTitle}
      onRequestClose={onRequestClose}
      handleAnimationEnd={handleAnimationEnd}
      onCloseEnd={() => {
        if (breakpoints?.isMobileSized) {
          closeBottomSheet()
        }
        onRequestClose()
      }}
      visible={isModalOpen}
      bottomSheetRef={ref}
      modalContents={modalContentsWithButtons}
      showCloseIcon
      useDynamicHeightForBottomSheet
      width={width}
      accessibilityLabel={formatMessage({
        defaultMessage: 'Access to Lyra benefit modal',
        description: 'Accessibility label for access to Lyra benefit modal dialog',
      })}
    />
  )
}
